import {
  ForgeDialog,
  ForgeScaffold,
  ForgeToolbar,
  ForgeButton,
  ForgeSelect,
  ForgeBanner
} from '@tylertech/forge-react';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import './custom-sort.scss';
import { ViewColumn } from 'common/types/viewColumn';
import { get, isEqual, isNull, isUndefined, map } from 'lodash';
import I18n from 'common/i18n';
import UniqueValuesContainer from './UniqueValuesContainer';
import { getAgTableHeaderName } from 'common/visualizations/views/agGridReact/helpers/TableColumnFormatting';
import {
  UniqueValuesData,
  MAX_CUSTOM_SORT_UNIQUE_ROW_COUNT,
  TableColumnFormat
} from 'common/visualizations/views/agGridReact/types';
import { CustomSortConfig, OrderConfig, SortDirection } from 'common/visualizations/vif';

import {
  setTableCustomSort,
  setTableOrderConfig,
  setTableHeaderSortCycleIndex
} from 'common/authoring_workflow/actions';
import { Dispatch } from 'redux';
import {
  getNextSort,
  HEADER_SORT_INDEX,
  updateSortConfig
} from 'common/visualizations/views/agGridReact/helpers/SortHelpers';
import eventBus from 'common/visualizations/views/agGridReact/helpers/EventBus';
import { withCustomSort } from './helpers';

const scope = 'shared.components.custom_sort.modal';

type CustomSortModalProps = {
  isCustomSortModalOpen: boolean;
  setIsCustomSortModalOpen: (isCustomSortModalOpen: any) => void;
  columnsToCustomSort: ViewColumn[];
  columnFormats: { [key: string]: TableColumnFormat };
  getUniqueValues?: (
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined
  ) => Promise<UniqueValuesData>;
  vifOrderConfig: OrderConfig[];
  hierarchyId?: string | undefined;
  customSortConfig?: CustomSortConfig;
  distinctValuesCountMap: object;
  isResetDisabled: boolean;
  setIsResetDisabled: (isResetDisabled: boolean) => void;
  isColumnSelectionDisabled: boolean;
  setIsColumnSelectionDisabled: (isColumnSelectionDisabled: boolean) => void;
  isClickedColumnAGrouping?: boolean;
};

type DispatchProps = {
  handleSetTableCustomSort: (
    hierarchyId: string | undefined,
    columnName: string | null,
    rowData: any[]
  ) => void;
  handleSetTableOrderConfig: (orderConfig: OrderConfig[], hierarchyId: string | undefined) => void;
  handleSetTableHeaderSortCycleIndex: (headerSortCycleIndex: number, hierarchyId: string | undefined) => void;
};

type Props = CustomSortModalProps & DispatchProps;

const CustomSortModal: React.FC<Props> = ({
  isCustomSortModalOpen,
  setIsCustomSortModalOpen,
  columnsToCustomSort,
  columnFormats,
  getUniqueValues,
  vifOrderConfig,
  hierarchyId,
  customSortConfig,
  handleSetTableCustomSort,
  handleSetTableOrderConfig,
  handleSetTableHeaderSortCycleIndex,
  distinctValuesCountMap,
  isResetDisabled,
  setIsResetDisabled,
  isColumnSelectionDisabled,
  setIsColumnSelectionDisabled,
  isClickedColumnAGrouping
}) => {
  const [uniqueValues, setUniqueValues] = useState<unknown[]>([]);
  const [isModalInitial, setIsModalInitial] = useState<boolean>(false);
  const [isApplyDisabled, setIsApplyDisabled] = useState<boolean>(true);
  const [initialUniqueValues, setInitialUniqueValues] = useState<any[]>([]);
  const [newUniqueValues, setNewUniqueValues] = useState<any[]>([]);

  const hideModal = () => {
    setIsCustomSortModalOpen(false);
  };

  const handleReset = () => {
    if (isResetDisabled) return;
    handleSetTableCustomSort(hierarchyId, columnToSort, []);
    const current = vifOrderConfig.find((col) => col.columnName === columnToSort);
    let originalSort: SortDirection = undefined;
    if (current?.ascending === true) {
      originalSort = 'ascending';
    } else if (current?.ascending === false) {
      originalSort = 'descending';
    }

    const updatedState = updateSortConfig({
      configs: vifOrderConfig,
      columnName: columnToSort as string,
      nextSort: originalSort,
      isMultiSort: true,
      useAscending: true
    });

    eventBus.dispatch('setOrderConfigForRecompute', { updatedOrderConfig: updatedState });
    handleSetTableOrderConfig(updatedState, hierarchyId);
    hideModal();
  };

  const handleApply = async (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (isApplyDisabled) return;

    const isMultiSort = event.metaKey || event.ctrlKey;
    const nextSort = getNextSort({
      orderConfig: vifOrderConfig,
      columnName: columnToSort as string,
      customSortConfig,
      isHeaderClick: false
    });

    const updatedState = updateSortConfig({
      configs: vifOrderConfig,
      columnName: columnToSort as string,
      nextSort,
      isMultiSort,
      useAscending: false,
      overrideSortToFollow: 'customSort'
    });
    eventBus.dispatch('setOrderConfigForRecompute', { updatedOrderConfig: updatedState });
    handleSetTableOrderConfig(updatedState, hierarchyId);
    handleSetTableCustomSort(hierarchyId, columnToSort, newUniqueValues);
    if (isClickedColumnAGrouping) {
      handleSetTableHeaderSortCycleIndex(HEADER_SORT_INDEX.CUSTOM_SORT, hierarchyId);
    }
    hideModal();
  };

  // TODO: NO-JIRA check if the modal's design is appropriate after testing
  // const computeModalOffset = () => {
  //   // Offset modal's y-position so it would be slightly off-center
  //   // based on figma design. The following formula should be good
  //   // for 1080p and above resolutions.
  //   return -0.4242 * window.outerHeight + 334.8;
  // };

  const dialogAttributes = {
    // customPosition: true,
    // positionX: 0,
    // positionY: computeModalOffset(),
    // positionType: 'relative'
  };

  const mappedColumnsToCustomSort = map(columnsToCustomSort, (col) => {
    const columnFormat = columnFormats?.[col.fieldName];
    const displayName = getAgTableHeaderName(columnFormat, col);
    return {
      label: displayName,
      value: col.fieldName
    };
  });

  const initialColumnToSort =
    mappedColumnsToCustomSort.length > 0 ? mappedColumnsToCustomSort[0].value : null;

  const [columnToSort, setColumnToSort] = useState<string | null>(initialColumnToSort);

  const getExistingCustomSortConfig = (col: string) => {
    return get(customSortConfig, col, []).map((value) => {
      if (isEqual(value, {})) {
        return {};
      }
      return {
        [col]: value
      };
    });
  };

  const setData = (colName: string, viewColumnParam?: ViewColumn | undefined) => {
    if (!getUniqueValues) {
      return;
    }
    const existingCustomSortConfig = getExistingCustomSortConfig(colName);
    const viewColumn = viewColumnParam ?? columnsToCustomSort[0];
    const uniqueRowCount = parseInt(get(distinctValuesCountMap, [viewColumn.fieldName], '0'));
    const sortToFollow = vifOrderConfig.find((col) => col.columnName === columnToSort)?.sortToFollow;
    if (
      existingCustomSortConfig.length === 0 ||
      uniqueRowCount !== existingCustomSortConfig.length ||
      sortToFollow !== 'customSort'
    ) {
      if (uniqueRowCount <= MAX_CUSTOM_SORT_UNIQUE_ROW_COUNT) {
        getUniqueValues([viewColumn], vifOrderConfig, customSortConfig).then((data) => {
          const rows = data.rows;
          setUniqueValues(rows);
          setInitialUniqueValues(rows);
        });
        if (!withCustomSort(sortToFollow, existingCustomSortConfig)) setIsResetDisabled(true);
      } else {
        setUniqueValues(existingCustomSortConfig);
        setInitialUniqueValues(existingCustomSortConfig);
      }
    } else {
      setUniqueValues(existingCustomSortConfig);
      setInitialUniqueValues(existingCustomSortConfig);
    }
  };

  const selectAttributes = {
    'data-testid': 'custom-sort-dialog-dropdown',
    options: mappedColumnsToCustomSort,
    value: columnToSort,
    label: I18n.t('dropdown_label', { scope }),
    floatLabelType: 'always',
    onChange: (event: CustomEvent) => {
      if (columnToSort === event.detail) return;
      const selectedColumn = columnsToCustomSort.find(
        (col) => col.fieldName.toLocaleLowerCase() === event.detail.toLocaleLowerCase()
      );
      if (!selectedColumn || !getUniqueValues) return;

      setColumnToSort(event.detail);
      setData(event.detail, selectedColumn);

      const isColumnWithCustomSortConfig =
        !isUndefined(customSortConfig) &&
        !isNull(columnToSort) &&
        customSortConfig.hasOwnProperty(event.detail) &&
        customSortConfig[event.detail].length != 0;
      if (isColumnWithCustomSortConfig) {
        setIsResetDisabled(false);
      } else {
        setIsResetDisabled(true);
      }
    }
  };

  useEffect(() => {
    if (!isModalInitial && isCustomSortModalOpen) {
      setIsModalInitial(true);
    }
    if (isModalInitial && getUniqueValues && initialColumnToSort) {
      setData(initialColumnToSort);
    }
  }, [isModalInitial]);

  const isUniqueRowCountInRange = columnToSort
    ? parseInt(get(distinctValuesCountMap, columnToSort, '0') as string, 10) <=
      MAX_CUSTOM_SORT_UNIQUE_ROW_COUNT
    : false;

  return (
    <ForgeDialog
      on-forge-dialog-close={hideModal}
      open={isCustomSortModalOpen}
      persistent={true}
      options={dialogAttributes}
      aria-labelledby="dialog-title"
    >
      <div data-testid="custom-sort-dialog">
        <ForgeScaffold className="custom-sort-scaffold">
          <ForgeToolbar slot="header" no-divider>
            <h1 id="dialog-title" slot="start" className="forge-typography--subheading4 custom-sort-title">
              {I18n.t('title', { scope })}
            </h1>
          </ForgeToolbar>
          <div slot="body" className="custom-sort-modal-body">
            {!isUniqueRowCountInRange && (
              <>
                <ForgeBanner theme={'warning'} persistent={true} id="custom-sort-modal-banner">
                  <span className="message-container">
                    <strong>{I18n.t('shared.components.custom_sort.modal.rows_exceeded_title')}</strong>
                    <br />
                    <br />
                    <span>{I18n.t('shared.components.custom_sort.modal.rows_exceeded_description')}</span>
                  </span>
                </ForgeBanner>
                <br />
              </>
            )}
            <ForgeSelect {...selectAttributes} disabled={isColumnSelectionDisabled} />
            <div style={{ marginTop: '16px', marginBottom: '2px' }}>
              <span>
                Rows (
                {columnToSort ? parseInt(get(distinctValuesCountMap, columnToSort, '0') as string, 10) : 0})
              </span>
            </div>
            {isCustomSortModalOpen && (
              <UniqueValuesContainer
                columnsToCustomSort={columnsToCustomSort}
                columnFormats={columnFormats}
                data={uniqueValues}
                isCustomSortModalOpen={isCustomSortModalOpen}
                columnToSort={columnToSort}
                setIsApplyDisabled={setIsApplyDisabled}
                initialUniqueValues={initialUniqueValues}
                setNewUniqueValues={setNewUniqueValues}
                setIsColumnSelectionDisabled={setIsColumnSelectionDisabled}
              />
            )}
          </div>
          <ForgeToolbar slot="footer" no-divider>
            <ForgeButton
              slot="start"
              className="custom-sort-modal-reset-button"
              onClick={handleReset}
              disabled={isResetDisabled}
            >
              {I18n.t('reset', { scope })}
            </ForgeButton>
            <div className="custom-sort-modal-footer-end" slot="end">
              <ForgeButton type="outlined" onClick={hideModal}>
                {I18n.t('cancel', { scope })}
              </ForgeButton>
              <ForgeButton type="unelevated" onClick={handleApply} disabled={isApplyDisabled}>
                {I18n.t('apply', { scope })}
              </ForgeButton>
            </div>
          </ForgeToolbar>
        </ForgeScaffold>
      </div>
    </ForgeDialog>
  );
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  handleSetTableCustomSort: (hierarchyId, columnName, rowData) =>
    dispatch(setTableCustomSort(hierarchyId, columnName, rowData)),
  handleSetTableOrderConfig: (orderConfig, hierarchyId) =>
    dispatch(setTableOrderConfig(orderConfig, hierarchyId)),
  handleSetTableHeaderSortCycleIndex: (headerSortCycleIndex, hierarchyId) =>
    dispatch(setTableHeaderSortCycleIndex(headerSortCycleIndex, hierarchyId))
});

export default connect(null, mapDispatchToProps)(CustomSortModal);
